import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { getElapsed } from '@sola/utils/relativeTime.ts';
import type { ContentFeedCardProps, ContentFeedMatch, ContentFeedMedia } from './content-feed-card.tsx';
const emptyMedia: ContentFeedMedia[] = [];

/**
 * Fallback dimensions for the Lightbox Image schema which requires numeric width/height.
 * These represent a standard 3:2 landscape ratio matching typical content feed imagery.
 * TODO: Remove once the upstream API guarantees dimensions on all media items.
 */
const FALLBACK_IMAGE_WIDTH = 700;
const FALLBACK_IMAGE_HEIGHT = 467;

/**
 * Derive banner text from the query match context.
 */
// TODO: we may not need this, not quire sure how the API will help us out here
const deriveBannerText = (match: ContentFeedMatch | undefined, t: ReturnType<typeof useTranslation>['t']): string | undefined => {
  if (!match) {
    return undefined;
  }
  const participantCount = match.related.participants?.length ?? 0;
  if (match.tier === 'fcp' && participantCount > 0) {
    return t('my:contentFeed.bannerFcp', {
      count: participantCount
    });
  }
  return undefined;
};
export const useComponentContentFeedCard = props => {
  const $ = _c(50);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    sourceType,
    icon: t1,
    title,
    description,
    media: t2,
    fundingProgress,
    link,
    programCountryName,
    match,
    bannerText,
    hasNewUpdate: t3,
    ctaLabel,
    publishedAt,
    author
  } = props;
  const icon = t1 === undefined ? "heart-circle" : t1;
  const media = t2 === undefined ? emptyMedia : t2;
  const hasNewUpdate = t3 === undefined ? false : t3;
  const isCausesLayout = sourceType === "causes" || !sourceType;
  const isPostLayout = sourceType === "community" || sourceType === "compassion.media";
  let authorLine;
  if (author) {
    authorLine = author.role ? `${author.displayName}, ${author.role}` : author.displayName;
  }
  let t4;
  if ($[1] !== publishedAt) {
    t4 = publishedAt ? getElapsed(publishedAt) : undefined;
    $[1] = publishedAt;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const elapsed = t4;
  const formattedPublishedAt = elapsed && typeof elapsed === "string" ? elapsed : undefined;
  let t5;
  if ($[3] !== media) {
    const imageMedia = media.filter(_temp);
    const videoMedia = media.filter(_temp2);
    t5 = [...imageMedia, ...videoMedia];
    $[3] = media;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const allMedia = t5;
  let t6;
  if ($[5] !== allMedia) {
    t6 = allMedia.slice(0, 3);
    $[5] = allMedia;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const galleryItems = t6;
  const totalPhotos = allMedia.length;
  let t7;
  if ($[7] !== bannerText || $[8] !== match || $[9] !== t) {
    t7 = bannerText ?? deriveBannerText(match, t);
    $[7] = bannerText;
    $[8] = match;
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const resolvedBannerText = t7;
  const progressPercent = Math.min(fundingProgress?.percentageFunded ?? 0, 100);
  let t8;
  if ($[11] !== fundingProgress || $[12] !== t) {
    t8 = fundingProgress ? t("core:numbers.currencyCustom", {
      amount: fundingProgress.raised
    }) : "";
    $[11] = fundingProgress;
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const formattedRaised = t8;
  let t9;
  if ($[14] !== fundingProgress || $[15] !== t) {
    t9 = fundingProgress ? t("core:numbers.currencyCustom", {
      amount: fundingProgress.goal
    }) : "";
    $[14] = fundingProgress;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const formattedGoal = t9;
  const isFundedOrClosed = fundingProgress?.status === "funded" || fundingProgress?.status === "closed";
  let t10;
  if ($[17] !== fundingProgress || $[18] !== isFundedOrClosed || $[19] !== t) {
    t10 = isFundedOrClosed && fundingProgress?.fundedDate ? t("my:contentFeed.fundedOn", {
      date: fundingProgress.fundedDate
    }) : undefined;
    $[17] = fundingProgress;
    $[18] = isFundedOrClosed;
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const fundedMessage = t10;
  let t11;
  if ($[21] !== allMedia || $[22] !== title) {
    t11 = allMedia.map(m_1 => {
      if (m_1.type === "video") {
        return {
          media: {
            video: {
              url: m_1.url,
              thumbnailUrl: m_1.thumbnailUrl,
              width: m_1.width,
              height: m_1.height
            },
            image: m_1.thumbnailUrl ? {
              url: m_1.thumbnailUrl,
              alt: m_1.alt || title,
              width: m_1.width ?? FALLBACK_IMAGE_WIDTH,
              height: m_1.height ?? FALLBACK_IMAGE_HEIGHT
            } : undefined
          }
        };
      }
      return {
        media: {
          image: {
            url: m_1.url,
            alt: m_1.alt || title,
            width: m_1.width ?? FALLBACK_IMAGE_WIDTH,
            height: m_1.height ?? FALLBACK_IMAGE_HEIGHT
          }
        }
      };
    });
    $[21] = allMedia;
    $[22] = title;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const lightboxItems = t11;
  let t12;
  if ($[24] !== ctaLabel || $[25] !== t) {
    t12 = ctaLabel ?? t("my:contentFeed.donate");
    $[24] = ctaLabel;
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const resolvedCtaLabel = t12;
  let t13;
  if ($[27] !== author || $[28] !== authorLine || $[29] !== description || $[30] !== formattedGoal || $[31] !== formattedPublishedAt || $[32] !== formattedRaised || $[33] !== fundedMessage || $[34] !== fundingProgress || $[35] !== galleryItems || $[36] !== hasNewUpdate || $[37] !== icon || $[38] !== isCausesLayout || $[39] !== isPostLayout || $[40] !== lightboxItems || $[41] !== link || $[42] !== programCountryName || $[43] !== progressPercent || $[44] !== resolvedBannerText || $[45] !== resolvedCtaLabel || $[46] !== t || $[47] !== title || $[48] !== totalPhotos) {
    t13 = {
      isCausesLayout,
      isPostLayout,
      author,
      authorLine,
      formattedPublishedAt,
      description,
      icon,
      title,
      fundingProgress,
      link,
      programCountryName,
      hasNewUpdate,
      galleryItems,
      totalPhotos,
      resolvedBannerText,
      progressPercent,
      formattedRaised,
      formattedGoal,
      fundedMessage,
      lightboxItems,
      resolvedCtaLabel,
      t
    };
    $[27] = author;
    $[28] = authorLine;
    $[29] = description;
    $[30] = formattedGoal;
    $[31] = formattedPublishedAt;
    $[32] = formattedRaised;
    $[33] = fundedMessage;
    $[34] = fundingProgress;
    $[35] = galleryItems;
    $[36] = hasNewUpdate;
    $[37] = icon;
    $[38] = isCausesLayout;
    $[39] = isPostLayout;
    $[40] = lightboxItems;
    $[41] = link;
    $[42] = programCountryName;
    $[43] = progressPercent;
    $[44] = resolvedBannerText;
    $[45] = resolvedCtaLabel;
    $[46] = t;
    $[47] = title;
    $[48] = totalPhotos;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  return t13;
};
function _temp(m) {
  return m.type === "image";
}
function _temp2(m_0) {
  return m_0.type === "video";
}